import currency from "currency.js"

/**
 * Calculates the tax amount for a given monetary amount and tax rate.
 *
 * @param {number} amount - The monetary amount to calculate tax on (e.g., 124.12).
 * @param {number} taxRate - The tax rate as a percentage (e.g., 7 for 7%).
 * @returns {number} - The calculated tax amount.
 *
 * @example
 * // returns 8.69
 * calculateTax(124.12, 7);
 */
function calculateTax(amount, taxRate) {
  // Convert tax rate to a decimal (e.g., 7% becomes 0.07)
  const taxDecimal = currency(taxRate).divide(100)

  // Calculate the tax amount
  const taxAmount = currency(amount).multiply(taxDecimal)

  // Return the tax amount as a number
  return taxAmount.value
}

/**
 * Calculates the total amount based on the subTotal, taxAmount, and discountAmount.
 *
 * @param {number} subTotal - The base monetary amount before taxes and discounts.
 * @param {number} taxAmount - The tax to be added to the subTotal.
 * @param {number} discountAmount - The discount to be subtracted from the total.
 * @returns {number} - The final calculated total.
 *
 * @example
 * // returns 110.00
 * calculateTotal(100, 10, 0);
 *
 * @example
 * // returns 105.50
 * calculateTotal(100, 7.50, 2);
 */
function calculateTotal(subTotal, taxAmount, discountAmount) {
  // Use currency.js to calculate the total
  const total = currency(subTotal).add(taxAmount).subtract(discountAmount)

  // Return the total amount as a number
  return total.value
}

/**
 * Converts a monetary value to its equivalent in cents.
 *
 * @param {number} amount - The monetary value to be converted (e.g., 1.00 for $1.00).
 * @returns {number} - The equivalent value in cents.
 *
 * @example
 * // returns 100
 * convertToCents(1.00);
 *
 * @example
 * // returns 12412
 * convertToCents(124.12);
 */
function convertToCents(amount) {
  // Use currency.js to multiply the amount by 100 and convert to cents
  return currency(amount).multiply(100).value
}

/**
 * Converts an amount in cents to its decimal dollar value.
 *
 * @param {number} amount - The amount in cents to convert.
 * @returns {number} The converted amount in decimal dollars.
 *
 * @example
 * // Returns 10.50
 * convertFromCents(1050);
 *
 * @example
 * // Returns 0.99
 * convertFromCents(99);
 *
 * @throws {Error} Throws an error if the currency function is not defined.
 * @throws {TypeError} Throws a TypeError if the input is not a number.
 */
function convertFromCents(amount) {
  return currency(amount).divide(100).value
}

/**
 * Calculates the discount for a given amount based on a percentage.
 *
 * @param {number} amount - The original amount of money.
 * @param {number} percentage - The discount percentage to be applied (e.g., 15 for 15%, 15.5 for 15.5%).
 * @returns {number} - The calculated discount in the same format as the amount.
 *
 * @example
 * // returns 150
 * calculateDiscount(1000, 15);
 *
 * @example
 * // returns 155
 * calculateDiscount(1000, 15.5);
 */
function calculatePercentageDiscount(amount, percentage) {
  const discount = currency(amount).multiply(percentage / 100)
  return discount.value
}

/**
 * Formats a given amount into a currency string based on the specified currency code and locale.
 *
 * @param {number} amount - The amount of money to format.
 * @param {string} currency - The currency code to format the amount in (e.g., 'USD', 'EUR', 'JPY').
 * @param {string} [locale='en-US'] - The locale to format the currency string (e.g., 'en-US', 'fr-FR'). Optional, defaults to 'en-US'.
 * @returns {string} The formatted currency string.
 *
 * @example
 * formatCurrency(1234.56, 'USD');
 * // Returns: "$1,234.56"
 *
 * @example
 * formatCurrency(9876.54, 'EUR', 'fr-FR');
 * // Returns: 9876,54 €"
 */
function formatCurrency(amount, currency = "USD", locale = "en-US") {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currency,
  }).format(amount)
}

/**
 * Converts an amount from cents to a standard currency format and returns the formatted value.
 *
 * @param {number} amount - The amount in cents to be converted.
 * @param {string} currency - The currency code (e.g., "USD", "EUR") used for formatting the value.
 * @returns {string} - The formatted currency value.
 */
function convertAndFormatCurrency(amount, currency = "USD") {
  return formatCurrency(convertFromCents(amount), currency.toUpperCase())
}

/**
 * Calculates the rate by dividing the amount by the quantity.
 * Uses `currency.js` to ensure precise calculations with proper rounding.
 *
 * @param {number} qty - The quantity. Must be greater than 0.
 * @param {number|string} amount - The total amount as a number or a string (e.g., "100.50").
 * @returns {number} - The calculated rate, rounded to 2 decimal places.
 */
function calculateRate(qty, amount) {
  return currency(amount).divide(qty).value
}

/**
 * Adds two monetary amounts using currency.js to ensure accurate calculations.
 *
 * @param {number|string} amount1 - The first monetary amount.
 * @param {number|string} amount2 - The second monetary amount.
 * @returns {number} - The sum of the two amounts as a number.
 */
function addAmounts(amount1, amount2) {
  return currency(amount1).add(amount2).value
}

/**
 * Formats a currency value in a compact 'k' format without cents.
 * For example, 134919 becomes '$134k', 1500 becomes '$1.5k'.
 * Small values under 1000 are displayed normally, e.g., 680 becomes '$680'.
 *
 * @param {number} amount - The amount to format.
 * @param {string} currencySymbol - The currency symbol to use (default: '$').
 * @returns {string} - The formatted currency string.
 *
 * @example
 * // returns '$134k'
 * formatCurrencyCompact(134919);
 *
 * @example
 * // returns '$1.5k'
 * formatCurrencyCompact(1500);
 *
 * @example
 * // returns '$680'
 * formatCurrencyCompact(680);
 *
 * @example
 * // returns '€10k'
 * formatCurrencyCompact(10000, '€');
 */
function formatCurrencyCompact(amount, currencySymbol = "$") {
  // For small values (less than 1000), display the regular currency format
  if (amount < 1000) {
    return `${currencySymbol}${amount.toLocaleString()}`
  }

  // Convert to thousands
  const thousands = amount / 1000

  // Format with one decimal place if not a whole number
  let formatted
  if (thousands === Math.floor(thousands)) {
    // No decimal places for whole numbers
    formatted = `${currencySymbol}${Math.floor(thousands)}k`
  } else {
    // One decimal place for non-whole numbers
    formatted = `${currencySymbol}${thousands.toFixed(1)}k`
  }

  return formatted
}

/**
 * Formats a given amount into a currency string without cents.
 *
 * @param {number} amount - The amount of money to format.
 * @param {string} currencyCode - The currency code to format the amount in (e.g., 'USD', 'EUR', 'JPY').
 * @param {string} [locale='en-US'] - The locale to format the currency string (e.g., 'en-US', 'fr-FR'). Optional, defaults to 'en-US'.
 * @returns {string} The formatted currency string without cents.
 *
 * @example
 * formatCurrencyWithoutCents(10000, 'USD');
 * // Returns: "$10,000"
 *
 * @example
 * formatCurrencyWithoutCents(9876, 'EUR', 'fr-FR');
 * // Returns: "9 876 €"
 */
function formatCurrencyWithoutCents(amount, currencyCode = "USD", locale = "en-US") {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currencyCode,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount)
}

export default {
  calculateTax,
  calculateTotal,
  convertToCents,
  convertFromCents,
  calculatePercentageDiscount,
  convertAndFormatCurrency,
  formatCurrency,
  formatCurrencyWithoutCents,
  calculateRate,
  addAmounts,
  formatCurrencyCompact,
}
